import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";

// Every heavy page is code-split so each route ships as its own chunk. First hit downloads
// only that page + shared vendors; subsequent visits hit the browser cache. Cuts initial
// bundle by ~70% and makes cross-page navigation near-instant after the first click.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DailyEntry = lazy(() => import("@/pages/DailyEntry"));
const EntryHistory = lazy(() => import("@/pages/EntryHistory"));
const MyDailyReport = lazy(() => import("@/pages/MyDailyReport"));
const Materials = lazy(() => import("@/pages/Materials"));
const Advances = lazy(() => import("@/pages/Advances"));
const MonthlyBills = lazy(() => import("@/pages/MonthlyBills"));
const CashDeposits = lazy(() => import("@/pages/CashDeposits"));
const PnL = lazy(() => import("@/pages/PnL"));
const Reports = lazy(() => import("@/pages/Reports"));
const Verification = lazy(() => import("@/pages/Verification"));
const Manage = lazy(() => import("@/pages/Manage"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const Backups = lazy(() => import("@/pages/Backups"));
const TeaBusiness = lazy(() => import("@/pages/TeaBusiness"));
const HREmployees = lazy(() => import("@/pages/HREmployees"));
const EmployeeLedger = lazy(() => import("@/pages/EmployeeLedger"));
const HRVault = lazy(() => import("@/pages/HRVault"));
const Sessions = lazy(() => import("@/pages/Sessions"));
const CentralKitchen = lazy(() => import("@/pages/CentralKitchen"));
const HRWorkforce = lazy(() => import("@/pages/HRWorkforce"));
const HRAttendance = lazy(() => import("@/pages/HRAttendance"));
const HRLoans = lazy(() => import("@/pages/HRLoans"));
const HRPayroll = lazy(() => import("@/pages/HRPayroll"));
const Activate = lazy(() => import("@/pages/Activate"));

const KITCHEN_PATHS = ["/kitchen", "/hr/attendance", "/hr/workforce", "/hr/employees", "/change-password"];

// Tiny loading indicator shown briefly while a lazy chunk streams in. Kept unstyled/minimal
// so it doesn't flash a heavy skeleton over sub-second loads.
function RouteFallback() {
  return (
    <div className="min-h-[300px] flex items-center justify-center">
      <div className="h-6 w-6 border-2 border-orange-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Protected({ children, adminOnly, managerOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-orange-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "kitchen" && !KITCHEN_PATHS.includes(location.pathname))
    return <Navigate to="/kitchen" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (managerOnly && user.role !== "manager") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/activate" element={<Activate />} />
          <Route
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/daily" element={<DailyEntry />} />
            <Route path="/backdate" element={<DailyEntry backdate />} />
            <Route path="/history" element={<EntryHistory />} />
            <Route path="/my-report" element={<Protected managerOnly><MyDailyReport /></Protected>} />
            <Route path="/materials" element={<Materials />} />
            <Route path="/advances" element={<Advances />} />
            <Route path="/tea" element={<TeaBusiness />} />
            <Route path="/hr/employees" element={<HREmployees />} />
            <Route path="/hr/employees/:eid/ledger" element={<EmployeeLedger />} />
            <Route path="/hr/vault" element={<Protected adminOnly><HRVault /></Protected>} />
            <Route path="/sessions" element={<Sessions />} />
            <Route path="/kitchen" element={<CentralKitchen />} />
            <Route path="/hr/workforce" element={<HRWorkforce />} />
            <Route path="/hr/attendance" element={<HRAttendance />} />
            <Route path="/hr/loans" element={<Protected adminOnly><HRLoans /></Protected>} />
            <Route path="/hr/payroll" element={<Protected adminOnly><HRPayroll /></Protected>} />
            <Route path="/bills" element={<Protected adminOnly><MonthlyBills /></Protected>} />
            <Route path="/deposits" element={<Protected><CashDeposits /></Protected>} />
            <Route path="/pnl" element={<Protected adminOnly><PnL /></Protected>} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/verification" element={<Protected adminOnly><Verification /></Protected>} />
            <Route path="/manage" element={<Protected adminOnly><Manage /></Protected>} />
            <Route path="/activity" element={<Protected adminOnly><ActivityLog /></Protected>} />
            <Route path="/backups" element={<Protected adminOnly><Backups /></Protected>} />
            <Route path="/change-password" element={<ChangePassword />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
